import { documentsInfo } from "@/info/documents";
import { getNameFromCode } from "@/utils/get-name-from-code";

interface DocumentItem {
	name: string;
	from?: string;
	isAuthorityVerified?: boolean;
}

interface SanitizeResult {
	info?: string;
	res: DocumentItem[];
}

function sanitize(obj: DocumentItem[]): SanitizeResult {
	const finalResult: SanitizeResult = { res: [] };

	let arr = [...obj.filter((d: DocumentItem) => d.name)];
	let la, lb, ca, cb, ocr, oca, mayHaveOCa;
	arr.forEach(d => {
		if (d.name === "occupancyCertificate") ocr = true;
		else if (d.name === "ocApplied") oca = true;
		else if (d.name.includes("aKhathaLand")) la = true;
		else if (d.name.includes("bKhathaLand")) lb = true;
		else if (d.name.includes("aKhatha")) {
			ca = true;
			mayHaveOCa = d.from === "eKhatha";
		} else if (d.name.includes("bKhatha")) cb = true;
	});
	let remove: string[] = [];
	if (ca || ocr) remove = [...(mayHaveOCa ? [] : ["ocApplied"]), "bKhatha"];
	else if (oca) remove = ["occupancyCertificate", "aKhatha"];
	else if (cb) remove = ["occupancyCertificate", "aKhatha"];
	else if (la) remove = ["occupancyCertificate", "aKhatha"];
	else if (lb) remove = ["ocApplied", "occupancyCertificate", "aKhatha", "aKhathaLand"];

	const removing = arr.filter((d: DocumentItem) => Boolean(remove.filter((r: string) => r === d.name).length));
	if (removing.length)
		finalResult.info = "Removing: " + removing.map(r => getNameFromCode(documentsInfo, r.name)).join(", ");

	finalResult.res = arr.filter((d: DocumentItem) => !remove.filter((r: string) => r === d.name).length);

	return finalResult;
}

interface KhataInfo {
	name: string;
	from: string;
	isAuthorityVerified: boolean;
}

interface SaveableKhataResult {
	constructionKhatha?: KhataInfo;
	landKhatha?: KhataInfo;
}

interface DocumentsDataPortal {
	constructionKhatha?: string;
	landKhatha?: string;
	approvalDocs: string[];
	basicDetailsFor?: string;
}

interface DocumentsInfoParamsPortal {
	propertyType?: string;
	data?: DocumentsDataPortal;
	possessionStatus?: string;
}

interface DocumentsData {
	constructionKhatha?: string;
	constructionKhathaAuthority?: string;
	landKhatha?: string;
	landKhathaAuthority?: string;
	approvalDocs: string[];
}

function getSaveableKhataInfo({
	constructionKhatha,
	landKhatha,
}: {
	constructionKhatha?: string;
	landKhatha?: string;
}): SaveableKhataResult {
	const res: SaveableKhataResult = {};

	// filter all construction related docs
	const constructionKhathaOptions = documentsInfo.filter(d => d.isConstructionDoc);

	// filter all land Khatha related docs
	const landKhathaOptions = documentsInfo.filter(d => d.isLandDoc);
	if (constructionKhatha) {
		const constructionKhathaInfo = constructionKhathaOptions.find(e => e.code === constructionKhatha);
		if (constructionKhathaInfo && constructionKhathaInfo.from) {
			res.constructionKhatha = {
				name: constructionKhathaInfo.code,
				from: constructionKhathaInfo.from,
				isAuthorityVerified: true,
			};
		}
	}
	if (landKhatha) {
		const landKhathaInfo = landKhathaOptions.find(e => e.code === landKhatha);
		if (landKhathaInfo && landKhathaInfo.from) {
			res.landKhatha = {
				name: landKhathaInfo.code,
				from: landKhathaInfo.from,
				isAuthorityVerified: true,
			};
		}
	}

	return res;
}

function getSaveableDocumentsInfoPortal({
	propertyType,
	data,
	possessionStatus,
}: DocumentsInfoParamsPortal): DocumentItem[] | { err: string; data?: DocumentsDataPortal } {
	if (!propertyType || propertyType === "plot" || !data) return { err: '"propertyType" and "data" are required', data };

	const { constructionKhatha, landKhatha, approvalDocs, basicDetailsFor = "" } = data;

	if (
		!constructionKhatha &&
		possessionStatus !== "readyToMoveIn" &&
		!["cancellationOfRawUnit", "updateProjectAndLocality"].includes(basicDetailsFor)
	) {
		return { err: "Please choose Construction khatha authority" };
	}

	if (!landKhatha && !["cancellationOfRawUnit", "updateProjectAndLocality"].includes(basicDetailsFor)) {
		return { err: "Please choose Land khatha authority" };
	}

	const { constructionKhatha: cKhatha, landKhatha: lKhatha } = getSaveableKhataInfo({
		constructionKhatha,
		landKhatha,
	});

	let documents: DocumentItem[] = [];

	if (approvalDocs.length) documents = approvalDocs.map((name: string) => ({ name }));
	if (cKhatha) documents.push(cKhatha);
	if (lKhatha) documents.push(lKhatha);

	return sanitize(documents).res;
}

function getFormDataFromSaveablePortal(documents: DocumentItem[]): DocumentsData {
	const result: DocumentsData = {
		approvalDocs: [],
	};

	documents.forEach(doc => {
		// Find document info from documentsInfo array
		const docInfo = documentsInfo.find(d => d.code === doc.name);

		// Check if it's a khatha document with authority verification
		if (doc.isAuthorityVerified && doc.from && docInfo?.isKhataDoc) {
			if (docInfo.isConstructionDoc) {
				result.constructionKhatha = doc.name;
			} else if (docInfo.isLandDoc) {
				result.landKhatha = doc.name;
			}
		} else {
			// It's an approval document
			result.approvalDocs.push(doc.name);
		}
	});

	return result;
}

function getSaveableDocumentsInfo(d?: DocumentsData) {
	if (!d) return [];

	const documents: DocumentItem[] = [];

	if (d.constructionKhatha && d.constructionKhathaAuthority) {
		documents.push({
			from: d.constructionKhathaAuthority,
			name: d.constructionKhatha,
			isAuthorityVerified: true,
		});
	}

	if (d.landKhatha && d.landKhathaAuthority) {
		documents.push({
			from: d.landKhathaAuthority,
			name: d.landKhatha,
			isAuthorityVerified: true,
		});
	}

	if (d.approvalDocs && d.approvalDocs.length) {
		documents.push(...d.approvalDocs.map(name => ({ name })));
	}

	return documents;
}

function getFormDataFromSaveable(documents: DocumentItem[]): DocumentsData {
	const result: DocumentsData = {
		approvalDocs: [],
	};

	documents.forEach(doc => {
		// Find document info from documentsInfo array
		const docInfo = documentsInfo.find(d => d.code === doc.name);

		// Check if it's a khatha document with authority verification
		if (doc.isAuthorityVerified && doc.from && docInfo?.isKhataDoc) {
			if (docInfo.isConstructionDoc) {
				result.constructionKhatha = doc.name;
				result.constructionKhathaAuthority = doc.from;
			} else if (docInfo.isLandDoc) {
				result.landKhatha = doc.name;
				result.landKhathaAuthority = doc.from;
			}
		} else {
			// It's an approval document (non-khatha documents)
			result.approvalDocs.push(doc.name);
		}
	});

	return result;
}

export {
	getFormDataFromSaveable,
	getFormDataFromSaveablePortal,
	getSaveableDocumentsInfo,
	getSaveableDocumentsInfoPortal,
	getSaveableKhataInfo,
	sanitize,
	type DocumentItem,
	type DocumentsData,
};
